import { useEffect, useState } from "react";
import { http, apiError } from "@/lib/api";
import { CalendarBlank, CaretLeft, CaretRight, Check, X, Moon, HeartStraight, Warning } from "@phosphor-icons/react";

/**
 * WeeklyHabitReport — 7-days × N-habits tick grid so the coach can spot
 * pattern misses at a glance ("james always misses water Tue+Wed"). Includes
 * sleep summary (total, avg, nights) and a wellness-freshness flag.
 * Week navigator (back/forward) lets the coach compare weeks.
 */
const DOW_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function WeeklyHabitReport({ clientId }) {
  const [weeksBack, setWeeksBack] = useState(0);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      setErr("");
      try {
        const { data } = await http.get(`/coach/clients/${clientId}/weekly-habit-grid?weeks_back=${weeksBack}`);
        setData(data);
      } catch (e) { setErr(apiError(e)); }
    })();
  }, [clientId, weeksBack]);

  if (err) return <div className="border border-[#FF3333] bg-[#FF3333]/10 px-3 py-2 text-xs text-[#FF3333]" data-testid="weekly-report-error">{err}</div>;
  if (!data) return null;

  const { habits, days, grid, sleep, wellness_logged_this_week, wellness_last_date, week_start, week_end } = data;
  const todayIso = new Date().toISOString().slice(0, 10);
  const isCurrentWeek = weeksBack === 0;

  return (
    <div className="id-card" data-testid="weekly-habit-report">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <CalendarBlank size={16} weight="bold" className="text-[#FF3333]" />
          <div className="id-label">Weekly habit breakdown</div>
          {isCurrentWeek && (
            <span className="text-[10px] font-manrope uppercase tracking-[0.15em] border border-[#FF3333] bg-[#FF3333]/10 text-[#FF3333] px-2 py-0.5" data-testid="weekly-report-current-badge">
              This Week
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setWeeksBack((w) => Math.min(12, w + 1))}
            disabled={weeksBack >= 12}
            className="p-2 border border-[#262626] text-zinc-400 hover:text-white hover:border-white disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="weekly-report-prev"
            aria-label="Previous week"
          >
            <CaretLeft size={14} weight="bold" />
          </button>
          <div className="text-[11px] font-manrope uppercase tracking-[0.15em] text-zinc-400 min-w-[110px] text-center" data-testid="weekly-report-range">
            {week_start.slice(5)} → {week_end.slice(5)}
          </div>
          <button
            onClick={() => setWeeksBack((w) => Math.max(0, w - 1))}
            disabled={weeksBack === 0}
            className="p-2 border border-[#262626] text-zinc-400 hover:text-white hover:border-white disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="weekly-report-next"
            aria-label="Next week"
          >
            <CaretRight size={14} weight="bold" />
          </button>
        </div>
      </div>

      {habits.length === 0 ? (
        <div className="text-zinc-500 text-sm font-manrope py-6 text-center" data-testid="weekly-report-no-habits">
          No habits set for this client yet.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-manrope" data-testid="weekly-report-grid">
            <thead>
              <tr>
                <th className="text-left py-2 pr-3 text-zinc-500 uppercase tracking-[0.15em] font-normal">Habit</th>
                {days.map((d, i) => {
                  const isToday = d === todayIso;
                  return (
                    <th
                      key={d}
                      className={"text-center py-2 px-1 uppercase tracking-[0.1em] font-normal " + (isToday ? "text-[#FF3333]" : "text-zinc-500")}
                    >
                      <div className="text-[10px]">{DOW_LABELS[i]}</div>
                      <div className="text-[9px] mt-0.5">{d.slice(8)}</div>
                    </th>
                  );
                })}
                <th className="text-center py-2 pl-2 text-zinc-500 uppercase tracking-[0.15em] font-normal">Wk</th>
              </tr>
            </thead>
            <tbody>
              {habits.map((h) => {
                const row = grid[h.id] || {};
                const wkScore = days.reduce((acc, d) => acc + (row[d] ? 1 : 0), 0);
                const rowColor = wkScore <= 2 ? "text-[#FF3333]" : wkScore >= 6 ? "text-[#00CC66]" : "text-zinc-300";
                return (
                  <tr key={h.id} className="border-t border-[#262626]" data-testid={`weekly-report-row-${h.id}`}>
                    <td className="py-2 pr-3 text-white truncate max-w-[180px]" title={h.label}>{h.label}</td>
                    {days.map((d) => {
                      const done = !!row[d];
                      return (
                        <td key={d} className="text-center py-2 px-1">
                          {done ? (
                            <Check size={14} weight="bold" className="inline text-[#00CC66]" data-testid={`weekly-report-cell-${h.id}-${d}-on`} />
                          ) : (
                            <X size={12} weight="bold" className="inline text-zinc-700" data-testid={`weekly-report-cell-${h.id}-${d}-off`} />
                          )}
                        </td>
                      );
                    })}
                    <td className={"text-center py-2 pl-2 font-bold " + rowColor} data-testid={`weekly-report-total-${h.id}`}>
                      {wkScore}/7
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Sleep + wellness inline summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-5">
        <div className="border border-[#262626] bg-[#0F0F0F] px-3 py-2.5 flex items-start gap-3" data-testid="weekly-report-sleep">
          <Moon size={16} weight="fill" className="text-indigo-400 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="id-label text-zinc-500 mb-0.5">Sleep this week</div>
            {sleep.nights_logged > 0 ? (
              <div className="text-sm text-white">
                <span className={sleep.avg_hours < sleep.minimum_per_night ? "text-[#FF3333] font-bold" : "text-[#00CC66] font-bold"} data-testid="weekly-report-sleep-avg">
                  {sleep.avg_hours}h avg
                </span>
                <span className="text-zinc-500"> · </span>
                <span>{sleep.total_hours}h total</span>
                <span className="text-zinc-500"> · </span>
                <span>{sleep.nights_logged}/7 nights</span>
              </div>
            ) : (
              <div className="text-sm text-zinc-500">No sleep hours logged this week.</div>
            )}
          </div>
        </div>
        <div className="border border-[#262626] bg-[#0F0F0F] px-3 py-2.5 flex items-start gap-3" data-testid="weekly-report-wellness">
          {wellness_logged_this_week ? (
            <HeartStraight size={16} weight="fill" className="text-[#00CC66] shrink-0 mt-0.5" />
          ) : (
            <Warning size={16} weight="fill" className="text-[#FF3333] shrink-0 mt-0.5" />
          )}
          <div className="min-w-0">
            <div className="id-label text-zinc-500 mb-0.5">Wellness this week</div>
            {wellness_logged_this_week ? (
              <div className="text-sm text-[#00CC66]">
                Logged {wellness_last_date ? "on " + wellness_last_date.slice(5) : ""}
              </div>
            ) : (
              <div className="text-sm text-[#FF3333]">Not logged — client got a Sunday push.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
